import type { Core } from "../..";
import type { SchemaType } from "./misc/types";

import fs from "node:fs";
import path from "node:path";

import kleur from "kleur";

import { head } from "./misc/ascii";
import {
	extractMergedSchemas,
	loadConfigTs,
	prettify,
	resolveInstancePath,
} from "./misc/utils";
import { parseSchemas } from "./parser";
import { parsers } from "./parsers-list";

/**
 * Generates the `autotypes.d.ts` file in the root of the instance project using
 * the instance schemas exported in `griddo.config.file`
 * The main usage of this file is for intellisense and autocompletion in the
 * instance when they work with the React components and schemas.
 * @documentation https://www.notion.so/griddoio/AutoTypes-e7420867dd2e489c8da34a493b763cd8?pvs=4
 */
(async () => {
	try {
		const instancePath = resolveInstancePath();
		const configFile = path.join(instancePath, "griddo.config.ts");
		let config: Core.Config;

		try {
			const loadedConfig = await loadConfigTs(configFile);
			// loadConfigTs may simply return the config, not { default: cfg } always, so handle both
			config =
				loadedConfig &&
				typeof loadedConfig === "object" &&
				"default" in loadedConfig
					? (loadedConfig as { default: Core.Config }).default
					: (loadedConfig as Core.Config);
		} catch (err) {
			console.error("Failed to load griddo.config.ts:", err);
			process.exit(1);
		}

		const {
			schemas: {
				config: { languages, menuItems, themes, subthemes, moduleCategories },
				contentTypes: { dataPacks, dataPacksCategories, structuredData },
				ui: { components, modules, templates },
				forms,
			},
		} = config;

		// These schemas are "merged" in the
		// `configFile.schemas.contentTypes.structuredData` props and we need to
		// get them separately.
		const { categoryContentTypes, pageContentTypes, simpleContentTypes } =
			extractMergedSchemas(config.schemas.contentTypes.structuredData);

		// In this case we merged some schemas in a `usSchemas` entity.
		const uiSchemas = {
			...components,
			...modules,
			...templates,
			...structuredData,
			...(forms?.templates || {}),
			...(forms?.fields || {}),
		} as Record<string, SchemaType>;

		const autotypes = parseSchemas([
			{ schema: menuItems,               parser: parsers.MenuItemsParser,           config }, // prettier-ignore
			{ schema: languages,               parser: parsers.LanguagesParser,           config }, // prettier-ignore
			{ schema: uiSchemas,               parser: parsers.UIAndDataPropsParser,      config }, // prettier-ignore
			{ schema: subthemes || {},         parser: parsers.SubthemesParser,           config }, // prettier-ignore
			{ schema: pageContentTypes,        parser: parsers.PageContentTypeParser,     config }, // prettier-ignore
			{ schema: simpleContentTypes,      parser: parsers.SimpleContentTypeParser,   config }, // prettier-ignore
			{ schema: categoryContentTypes,    parser: parsers.TaxonomyParser,            config }, // prettier-ignore
			{ schema: components,              parser: parsers.ComponentsNameParser,      config }, // prettier-ignore
			{ schema: modules,                 parser: parsers.ModulesNameParser,         config }, // prettier-ignore
			{ schema: forms?.templates || {},  parser: parsers.FormTemplatesParser,       config }, // prettier-ignore
			{ schema: forms?.fields || {},     parser: parsers.FormFieldsParser,          config }, // prettier-ignore
			{ schema: forms?.categories || [], parser: parsers.FormCategoryNamesParser,   config }, // prettier-ignore
			{ schema: templates,               parser: parsers.TemplatesNameParser,       config }, // prettier-ignore
			{ schema: templates,               parser: parsers.DataTemplatesNameParser,   config }, // prettier-ignore
			{ schema: templates,               parser: parsers.TemplateSectionListParser, config }, // prettier-ignore
			{ schema: moduleCategories,        parser: parsers.ModuleCateogiresParser,    config }, // prettier-ignore
			{ schema: dataPacks,               parser: parsers.DataPacksParser,           config }, // prettier-ignore
			{ schema: dataPacksCategories,     parser: parsers.DataPacksCategoryParser,   config }, // prettier-ignore
			{ schema: categoryContentTypes,    parser: parsers.TaxonomyPropsParser,       config }, // prettier-ignore
			{ schema: simpleContentTypes,      parser: parsers.SimpleDataPropsParser,     config }, // prettier-ignore
			{ schema: pageContentTypes,        parser: parsers.PageDataPropsParser,       config }, // prettier-ignore
			{ schema: themes,                  parser: parsers.ThemesParser,              config }, // prettier-ignore
		]);

		const outputContent = await prettify(`
			${head}
			${autotypes.join("\n")}
		`);

		const outputFile = path.join(instancePath, "autotypes.d.ts");
		fs.writeFileSync(outputFile, outputContent);

		console.log(`${kleur.green(" ✔")} AutoTypes`);
	} catch (e) {
		console.log(`${kleur.red(" ✘")} AutoTypes`);
		console.log(e);
	}
})();
